import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { createSelector } from '@reduxjs/toolkit';

import {
    getActiveStageId,
    getAspectRatio,
    getCaptionsColorOverrides,
    getCaptionsFontIndex,
    getCaptionsTextSize,
    getImageStyleGuidelines,
    getIngredients,
    getIsAnimationEditorActive,
    getIsAnyDefinedIngredientTransforming,
    getIsGeneratingScript,
    getIsPreparingStyle,
    getPresets,
    getSelectedCaptionsSceneDesignId,
    getSelectedPresetId,
    getSelectedTemplateId,
    getSceneShots,
    getSelectedVoiceSlug,
    getShots,
    getStyleReferenceAssets,
    getTargetDuration,
    getVideoNegativePrompt,
    getVideoStyleGuidelines,
} from './genx-slice';

import {
    ANIMATION_DATA_VERSION,
    type AnimationData,
    isCompleteImageEntry,
    isCompleteVideoEntry,
    type Shot,
} from 'js/src/components/animated-explainer-videos/types';
import {
    getSceneDesign,
    getTemplateDefaultCutawaySceneDesign,
    getTemplateEnabledGlobalTemplates,
} from 'js/src/data/templates/templates-selectors';
import { getContentSource, getScript } from 'js/src/data/video-content-input/selectors';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { RootState } from 'js/src/libs/RootState';

/** The shot a creator scene is edited as, once it has one. */
export function getSceneShot(state: RootState, sceneId: string): Shot | null {
    const shotId = getSceneShots(state)[sceneId];
    return getShots(state).find((shot) => shot.id === shotId) ?? null;
}

// Matches the GenX image models' configured limit (media/gen_ai/model_utils.py),
// used only until the real config is available.
const FALLBACK_MAX_REFERENCE_IMAGES = 10;

const reportedMissingModelConfigs = new Set<string>();

export function getMaxReferenceImages(state: RootState, modelName: string): number {
    const models = state.media.ai_media_models;
    const model = models.find((m) => m.model_name === modelName);
    if (!model) {
        if (models.length > 0 && !reportedMissingModelConfigs.has(modelName)) {
            reportedMissingModelConfigs.add(modelName);
            ErrorReporter.capture(new Error(`Model config not found for ${modelName}`));
        }
        return FALLBACK_MAX_REFERENCE_IMAGES;
    }
    return model.max_reference_images;
}

// GenX always generates video with Gemini Omni Flash (see DEFAULT_VIDEO_MODEL),
// which only produces landscape and vertical output.
export const GENX_DISABLED_ASPECT_RATIOS: ReadonlySet<AspectRatio> = new Set([
    VideoData.SQUARE_ASPECT_RATIO,
    VideoData.FOURFIVE_ASPECT_RATIO,
]);

export const getSelectedPresetTemplateId = createSelector(
    getPresets,
    getSelectedPresetId,
    (presets, selectedPresetId): string | null => {
        if (!selectedPresetId) return null;
        const preset = presets.find((p) => p.id === selectedPresetId);
        return preset?.templateId ?? null;
    },
);

export const getSelectedPresetCutawaySceneDesignId = (state: RootState): string | null => {
    const templateId = getSelectedPresetTemplateId(state);
    if (!templateId) return null;
    return getTemplateDefaultCutawaySceneDesign(state, templateId);
};

export const getEffectiveTemplateId = createSelector(
    getSelectedTemplateId,
    getSelectedPresetTemplateId,
    (override, presetTemplateId): string | null => override ?? presetTemplateId,
);

export const getEffectiveCutawaySceneDesignId = (state: RootState): string | null => {
    const override = getSelectedTemplateId(state);
    if (override) {
        return getTemplateDefaultCutawaySceneDesign(state, override);
    }
    return getSelectedPresetCutawaySceneDesignId(state);
};

/**
 * Returns true when the user's persisted captions scene-design id is valid
 * for the currently effective template. A persisted choice can become invalid
 * when the user switches templates after picking captions — template-specific
 * designs only apply to their owning template, and global designs only apply
 * to templates that list the owning global template in
 * `enabled_global_templates` (same gate as the backend's `sceneDesignsList`
 * endpoint).
 *
 * When the persisted scene design hasn't been loaded into the redux ORM yet
 * (e.g. modal opened mid-fetch), we can't verify and return true so the user's
 * choice isn't transiently nulled out — the selector re-runs as data lands.
 */
export const isPersistedCaptionsValidForEffectiveTemplate = (state: RootState): boolean => {
    const persistedSelection = getSelectedCaptionsSceneDesignId(state);
    const effectiveTemplateId = getEffectiveTemplateId(state);
    if (!persistedSelection || !effectiveTemplateId) return false;
    const sceneDesign = getSceneDesign(state, persistedSelection);
    if (!sceneDesign) return true;
    if (sceneDesign.template === effectiveTemplateId) return true;
    return getTemplateEnabledGlobalTemplates(state, effectiveTemplateId).includes(
        sceneDesign.template,
    );
};

export function getIngredientById(state: RootState, id: string) {
    return state.animated_explainer_videos.genx.ingredients.find((ing) => ing.id === id);
}

export function getIsGeneratingDefinedIngredientTransform(state: RootState, id: string): boolean {
    return (
        state.animated_explainer_videos.genx.isGeneratingDefinedIngredientTransformById[id] ?? false
    );
}

export function getDidDerivedIngredientGenerationFail(state: RootState, id: string): boolean {
    return state.animated_explainer_videos.genx.derivedIngredientGenerationFailedById[id] ?? false;
}

export function getIsGeneratingDefinedIngredientTransformById(
    state: RootState,
): Record<string, boolean> {
    return state.animated_explainer_videos.genx.isGeneratingDefinedIngredientTransformById;
}

export function getDerivedIngredientGenerationFailedById(
    state: RootState,
): Record<string, boolean> {
    return state.animated_explainer_videos.genx.derivedIngredientGenerationFailedById;
}

export function getIsIngredientStagePrepComplete(state: RootState): boolean {
    const genx = state.animated_explainer_videos.genx;
    if (genx.shots.length === 0) return false;
    // A derived ingredient is still "preparing" only while its reference image
    // is in flight. One whose generation failed is settled (kept with a retry
    // affordance), so it must not hold prep open forever.
    const failedById = genx.derivedIngredientGenerationFailedById;
    if (
        genx.ingredients.some(
            (ing) => ing.source === 'derived' && !ing.referenceAsset && !failedById[ing.id],
        )
    ) {
        return false;
    }
    return true;
}

export const getRegeneratingImageShotIds = createSelector(getShots, (shots) => {
    const ids = new Set<string>();
    for (const shot of shots) {
        if (shot.imageHistory.some((e) => e.isGenerating)) {
            ids.add(shot.id);
        }
    }
    return ids;
});

export const getRegeneratingVideoShotIds = createSelector(getShots, (shots) => {
    const ids = new Set<string>();
    for (const shot of shots) {
        if (shot.videoHistory.some((e) => e.isGenerating)) {
            ids.add(shot.id);
        }
    }
    return ids;
});

export const getIsAnyGenXAssetGenerating = createSelector(
    [getShots, getIsGeneratingScript, getIsAnyDefinedIngredientTransforming, getIsPreparingStyle],
    (shots, isGeneratingScript, isAnyDefinedIngredientTransforming, isPreparingStyle) =>
        isGeneratingScript ||
        isAnyDefinedIngredientTransforming ||
        isPreparingStyle ||
        shots.some(
            (shot) =>
                shot.imageHistory.some((e) => e.isGenerating) ||
                shot.videoHistory.some((e) => e.isGenerating),
        ),
);

// A shot is errored if any history entry has an error and is not still generating.
// Errored entries are cleared from history when the user interacts with the shot
// (e.g. retries), so any lingering errored entry indicates an unresolved error.
export const getErroredShotIds = createSelector(getShots, (shots) => {
    const ids = new Set<string>();
    for (const shot of shots) {
        const imageErrored = shot.imageHistory.some((e) => !!e.errorMessage && !e.isGenerating);
        const videoErrored = shot.videoHistory.some((e) => !!e.errorMessage && !e.isGenerating);
        if (imageErrored || videoErrored) ids.add(shot.id);
    }
    return ids;
});

function stripTransientEntries(shot: Shot): Shot {
    return {
        ...shot,
        imageHistory: shot.imageHistory.filter((e) => isCompleteImageEntry(e)),
        videoHistory: shot.videoHistory.filter((e) => isCompleteVideoEntry(e)),
        selectedImageEntryId: null,
        selectedVideoEntryId: null,
    };
}

/**
 * Utility function to get storyboard data as a plain object.
 * Not a selector - creates a new object on each call.
 * Use when you need a snapshot of the storyboard data.
 */
export const getAnimationData = (state: RootState): AnimationData => ({
    version: ANIMATION_DATA_VERSION,
    isAnimationEditorActive: true,
    aspectRatio: getAspectRatio(state),
    shots: getShots(state).map(stripTransientEntries),
    styleReferenceAssets: getStyleReferenceAssets(state),
    ingredients: getIngredients(state),
    selectedVoiceSlug: getSelectedVoiceSlug(state),
    selectedCaptionsSceneDesignId: getSelectedCaptionsSceneDesignId(state),
    captionsFontIndex: getCaptionsFontIndex(state),
    captionsColorOverrides: getCaptionsColorOverrides(state),
    captionsTextSize: getCaptionsTextSize(state),
    selectedTemplateId: getSelectedTemplateId(state),
    imageStyleGuidelines: getImageStyleGuidelines(state),
    videoStyleGuidelines: getVideoStyleGuidelines(state),
    videoNegativePrompt: getVideoNegativePrompt(state),
    selectedPresetId: getSelectedPresetId(state),
    script: getScript(state),
    contentSource: getContentSource(state),
    targetDuration: getTargetDuration(state),
    activeStageId: getActiveStageId(state),
    sceneShots: getSceneShots(state),
});

/**
 * The record the toolbox writes back to the video: the editor's snapshot, but keeping the
 * video's own editor flag so a toolbox video never routes into the animation editor.
 */
export const getToolboxAnimationData = (state: RootState): AnimationData => ({
    ...getAnimationData(state),
    isAnimationEditorActive: getIsAnimationEditorActive(state),
});

/** Whether the video holds anything the toolbox's animated explainer flow has written. */
export const hasToolboxAnimationData = (state: RootState): boolean =>
    getSelectedPresetId(state) !== undefined ||
    getIngredients(state).length > 0 ||
    getShots(state).length > 0;
